import { InjectionToken } from '@angular/core';
import { RoundFn } from '@marketiq/calc-core';
import { AutoCalcDispatcher } from './adapters/ngxs.adapter';
import { ColumnIdMap } from './adapters/column-id-map';
import { CatalogStatePort, LocaleContext, MessagePort, UomPort } from './ports/auto-calc-ports';

/**
 * Configuration for {@link AutoCalculationModule.forRoot}. The host supplies the bindings that
 * keep calc-core pure: the FieldKey↔control map, the locale-aware rounding pipeline, and the
 * side-effect dispatcher.
 */
export interface AutoCalculationConfiguration {
  /** Base URL for any HTTP-backed side effects (e.g. availability auto-calc service). */
  baseUrl?: string;
  /** Resolves FieldKeys to host FormGroup control keys. REQUIRED. */
  columnIdMap: ColumnIdMap;
  /**
   * Locale-aware rounding the rules delegate to. The host should pass its real
   * `formatNumber`-based pipeline so computed values are byte-identical to today's; when omitted,
   * calc-core's `defaultRound` (2 decimals) is used.
   */
  round?: RoundFn;
  /** Lease-term basis derived from locale: US -> 'months', UK/NL -> 'years'. */
  leaseTermBasis?: 'months' | 'years';
  /** Side-effect dispatcher (wraps NGXS Store). Optional. */
  dispatcher?: AutoCalcDispatcher;
  /**
   * Host-capability ports for the decoupling refactor (so listener orchestration can move into this
   * package). All optional — additive; a binding only needs the ports it actually uses. See
   * {@link UomPort}, {@link MessagePort}, {@link CatalogStatePort}, {@link LocaleContext}.
   */
  uom?: UomPort;
  messages?: MessagePort;
  catalogState?: CatalogStatePort;
  locale?: LocaleContext;
}

/**
 * Injection token for {@link AutoCalculationConfiguration}. Mirrors stacking-plan's
 * `STACKING_PLAN_CONFIGS_TOKEN`.
 */
export const AUTO_CALC_CONFIGS_TOKEN = new InjectionToken<AutoCalculationConfiguration>('AUTO_CALC_CONFIGS_TOKEN');
